import { useContext, useEffect, useRef, useState } from "react";
import { AiTwotonePrinter } from "react-icons/ai";
import { useIntl } from "react-intl";
import generatePDF, { Margin } from "react-to-pdf";
import {
  ConditionMethodEnum,
  FieldTypeEnum,
  PriceEnum,
} from "../../../enum/FormConfigEnum";
import { AuthContext } from "../../../routes/AuthProvider";
import { globalLoadingDispatch } from "../../../utils/DispatchEvent";
import { isDefined, isNonEmptyString } from "../../../utils/ValidationHelp";
import {
  IAttachmentSetting,
  IDropdownSetting,
  IInputFieldSetting,
} from "../models/response/IGetFormResponse";
import { IGetQuotationResponse } from "../models/response/IGetQuotationResponse";
import { IConfigWithValue } from "./Quote";

export const QuotationPreview = ({
  quotation,
  formConfigWithValue,
  printError,
  isShowInvoice,
}: {
  quotation?: IGetQuotationResponse;
  isShowInvoice?: boolean;
  printError: () => void;
  formConfigWithValue: IConfigWithValue[][];
}) => {
  const auth = useContext(AuthContext);
  const [isInvoice, setIsInvoice] = useState(false);
  const intl = useIntl();
  let locale = intl.locale as "en" | "zh" | "ms";
  const printRef = useRef<HTMLDivElement>(null);
  const section1Ref = useRef<HTMLDivElement>(null);
  const section2Ref = useRef<HTMLDivElement>(null);
  const additionalPaddingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isShowInvoice) {
      setIsInvoice(true);
    }
  }, [isShowInvoice]);

  const totalRef = { current: 0 };

  const getDropdownFieldValue = (field: IConfigWithValue) => {
    return (field.fieldSetting as Array<IDropdownSetting>)[
      isDefined(field.value) && !isNaN(parseInt(field.value!.toString()))
        ? parseInt(field.value!.toString())
        : 0
    ];
  };

  const getField = (fieldId: number) => {
    const formConfigPageMerge = formConfigWithValue.flatMap((config) => config);

    return formConfigPageMerge.find((field) => field.fieldId === fieldId);
  };

  const checkIsConditionMet = (fieldId: number) => {
    const field = getField(fieldId);

    const fieldCondition = field?.condition
      .filter((cond) => isNonEmptyString(cond.value) && isDefined(cond.fieldId))
      .map(
        (cond) => cond.fieldId && cond.value === getField(cond.fieldId)?.value
      );
    return field?.conditionMethod === ConditionMethodEnum.Or
      ? fieldCondition?.some((value) => value)
      : fieldCondition?.every((value) => value);
  };

  const findPricePerUnit = (field: IConfigWithValue) => {
    return field.fieldType === FieldTypeEnum.Input
      ? (field.fieldSetting as IInputFieldSetting).chargeConditions?.find(
          (condition) => {
            if (!isNonEmptyString(field.value)) return false;

            const fieldValue = parseFloat(field.value!.toString());
            const minValues =
              (field.fieldSetting as IInputFieldSetting).chargeConditions?.map(
                (c) => c.min
              ) || [];
            const smallestMin = Math.min(...minValues);

            const effectiveValue =
              fieldValue < smallestMin ? smallestMin : fieldValue;

            return (
              condition.min <= effectiveValue &&
              (!isDefined(condition.max) ||
                (isDefined(condition.max) && condition.max! > effectiveValue))
            );
          }
        )?.chargePerIncrement ?? 0
      : field.fieldType === FieldTypeEnum.Attachment
      ? (field.fieldSetting as IAttachmentSetting).fee ?? 0
      : getDropdownFieldValue(field).fee ?? 0;
  };

  const handlePrint = async () => {
    if (!printRef?.current) return;
    globalLoadingDispatch(true);

    printRef.current.style.width = "950px";
    const a4Height = 1380;
    if (additionalPaddingRef.current) {
      additionalPaddingRef.current.classList.remove("py-14");
      additionalPaddingRef.current.classList.add("py-8");
    }

    if (section2Ref.current && section1Ref.current) {
      const pageCount = Math.ceil(section1Ref.current.clientHeight / a4Height);
      const minPage = Math.floor(section1Ref.current.clientHeight / a4Height);

      if (
        pageCount * a4Height <
        section2Ref.current?.clientHeight + section1Ref.current?.clientHeight
      )
        section2Ref.current.style.paddingTop = `${
          a4Height - (section1Ref.current.clientHeight - minPage * a4Height)
        }px`;
    }

    const onFinish = () => {
      if (printRef?.current) printRef.current.style.width = "auto";
      if (section2Ref.current) section2Ref.current.style.paddingTop = "0px";
      if (additionalPaddingRef.current) {
        additionalPaddingRef.current.classList.remove("py-8");
        additionalPaddingRef.current.classList.add("py-14");
      }
      globalLoadingDispatch(false);
    };

    generatePDF(printRef, {
      filename: isInvoice
        ? "myBuka_ProformaInvoice.pdf"
        : "myBuka_Quotation.pdf",
      page: {
        margin: Margin.SMALL,
        format: "A4",
      },
    })
      .then(onFinish)
      .catch(onFinish);
  };

  const TableRow = ({
    label,
    value,
    valueClass = "",
    isHide = false,
  }: {
    label: string;
    value?: string;
    valueClass?: string;
    isHide?: boolean;
  }) => (
    <tr>
      <td className="w-1/3 font-bold tracking-wide uppercase md:w-auto">
        {label}
      </td>
      <td className="px-1">{!isHide ? ":" : ""}</td>
      <td className={`break-words ${valueClass}`}>{value ?? "-"}</td>
    </tr>
  );

  const formatPayment = (total?: number) => {
    return total?.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="max-w-screen-lg mx-auto">
      <AiTwotonePrinter
        size={25}
        className="absolute text-blue-500 cursor-pointer right-4 top-4 hover:text-blue-300"
        title={intl.formatMessage({
          id: isInvoice ? "printMyProformaInvoice" : "printMyQuote",
        })}
        onClick={handlePrint as any}
      />
      <div ref={printRef} className="mt-6 md:mt-0">
        <div ref={section1Ref}>
          <div className="flex items-end justify-between pb-2">
            <div className="text-xl font-bold md:text-2xl">
              {intl.formatMessage({
                id: isInvoice ? "proformaInvoice" : "quotation",
              })}
            </div>
            <img
              src="/image/logo.png"
              className="w-full md:max-w-52 max-w-48"
            />
          </div>
          <div className="items-end md:flex">
            <div className="w-full md:w-1/2 md:pe-4">
              <table className="w-full">
                <tbody>
                  <TableRow
                    label="ATTN."
                    value={quotation?.data?.name ?? auth?.userDetails?.name}
                  />
                  <TableRow
                    label={intl.formatMessage({ id: "contactNo" })}
                    value={
                      quotation?.data?.phoneNumber ??
                      auth?.userDetails?.phoneNumber
                    }
                  />
                  <TableRow
                    label={intl.formatMessage({ id: "email" })}
                    value={quotation?.data?.email ?? auth?.userDetails?.email}
                  />
                  <TableRow
                    label={intl.formatMessage({ id: "billTo" })}
                    value={
                      quotation?.data?.billingTo ?? auth?.userDetails?.billingTo
                    }
                  />
                  <TableRow
                    label=""
                    isHide={true}
                    value={
                      quotation?.data?.billingAddress ??
                      auth?.userDetails?.billingAddress
                    }
                  />
                </tbody>
              </table>
            </div>
            <div className="w-full md:w-1/2">
              <table className="w-full">
                <tbody>
                  <TableRow
                    label={intl.formatMessage({ id: "projectSite" })}
                    value={
                      quotation?.data?.siteAddress ??
                      auth?.userDetails?.siteAddress
                    }
                  />
                  <TableRow label="" value="&nbsp;" isHide={true} />
                  <TableRow
                    label={intl.formatMessage({
                      id: isInvoice ? "proformaInvNo" : "quotationNo",
                    })}
                    value={
                      isInvoice
                        ? quotation?.data?.invoiceId ?? auth?.invoiceNo
                        : quotation?.data?.quoteId ?? auth?.quoteNo
                    }
                  />
                  <TableRow
                    label={intl.formatMessage({ id: "date" })}
                    valueClass="uppercase tracking-wide"
                    value={(quotation?.data?.createdDate
                      ? new Date(quotation?.data?.createdDate)
                      : new Date()
                    ).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  />
                  <TableRow
                    label={intl.formatMessage({ id: "paymentTerm" })}
                    valueClass="uppercase tracking-wide"
                    value={intl.formatMessage({
                      id: "fullPaymentUponConfirmation",
                    })}
                  />
                </tbody>
              </table>
            </div>
          </div>
          <div className="py-4 my-4 font-bold tracking-wide uppercase border border-l-0 border-r-0 border-black">
            {intl.formatMessage({ id: "reinstatementDetailDescription" })}
          </div>
          {formConfigWithValue
            .flatMap((page) => page)
            .filter((field) => checkIsConditionMet(field.fieldId))
            .filter((field) => {
              if (field.fieldType === FieldTypeEnum.Dropdown) {
                return (
                  (field.fieldSetting as Array<IDropdownSetting>).some(
                    (dropdown) =>
                      dropdown.pricing !== PriceEnum.None &&
                      isNonEmptyString(dropdown.fee)
                  ) && isDefined(field.value)
                );
              }
              if (field.fieldType === FieldTypeEnum.Input) {
                return (
                  (
                    (
                      field.fieldSetting as IInputFieldSetting
                    )?.chargeConditions?.filter(
                      (charge) => charge.chargePerIncrement > 0
                    ) ?? []
                  ).length > 0 && isNonEmptyString(field.value)
                );
              }
              if (field.fieldType === FieldTypeEnum.Attachment) {
                return (
                  (field.fieldSetting as IAttachmentSetting).pricing !==
                    PriceEnum.None &&
                  isNonEmptyString(
                    (field.fieldSetting as IAttachmentSetting).fee
                  )
                );
              }
              return false;
            })
            .map((field) => {
              const minValues =
                (
                  field.fieldSetting as IInputFieldSetting
                ).chargeConditions?.map((c) => c.min) || [];
              const smallestMin = Math.min(...minValues);

              const count =
                field.fieldType === FieldTypeEnum.Input
                  ? parseFloat(field.value!.toString()) > smallestMin
                    ? parseFloat(field.value!.toString())
                    : smallestMin
                  : 1;

              const dispCount =
                field.fieldType === FieldTypeEnum.Input
                  ? parseFloat(field.value!.toString())
                  : 1;

              let pricePerUnit = 0;

              if (
                (field.fieldType === FieldTypeEnum.Dropdown &&
                  getDropdownFieldValue(field).pricing ===
                    PriceEnum.Percentage) ||
                (field.fieldType === FieldTypeEnum.Attachment &&
                  (field.fieldSetting as IAttachmentSetting).pricing ===
                    PriceEnum.Percentage)
              ) {
                pricePerUnit =
                  totalRef.current * (findPricePerUnit(field) / 100);

                totalRef.current += pricePerUnit * count;
              }
              if (
                field.fieldType === FieldTypeEnum.Input ||
                (field.fieldType === FieldTypeEnum.Dropdown &&
                  getDropdownFieldValue(field).pricing === PriceEnum.Total) ||
                (field.fieldType === FieldTypeEnum.Attachment &&
                  (field.fieldSetting as IAttachmentSetting).pricing ===
                    PriceEnum.Total)
              ) {
                pricePerUnit = findPricePerUnit(field);

                totalRef.current += findPricePerUnit(field) * count;
              }

              return (
                <div
                  className="grid grid-cols-2 gap-4 py-2"
                  key={field.fieldId}
                >
                  <div>
                    {isNonEmptyString(field.fieldLabel[locale])
                      ? field.fieldLabel[locale]
                      : field.fieldLabel.en}
                  </div>
                  <div>
                    {field.fieldType === FieldTypeEnum.Dropdown
                      ? isNonEmptyString(
                          getDropdownFieldValue(field).label[locale]
                        )
                        ? getDropdownFieldValue(field).label[locale]
                        : getDropdownFieldValue(field).label.en
                      : dispCount}
                  </div>
                </div>
              );
            })}

          <div className="mt-10">
            <div className="text-sm font-bold tracking-wide uppercase">
              {intl.formatMessage({ id: "generalReinstatementJobScope" })}
            </div>
            <table className="text-xs">
              <tbody>
                <tr>
                  <td valign="top" className="pe-2">
                    1.{" "}
                  </td>
                  <td>
                    {intl.formatMessage({ id: "reinstatementJobScope1" })}
                  </td>
                </tr>
                <tr>
                  <td valign="top" className="pe-2">
                    2.{" "}
                  </td>
                  <td>
                    {intl.formatMessage({ id: "reinstatementJobScope2" })}
                  </td>
                </tr>
                <tr>
                  <td valign="top" className="pe-2">
                    3.{" "}
                  </td>
                  <td>
                    {intl.formatMessage({ id: "reinstatementJobScope3" })}
                  </td>
                </tr>
                <tr>
                  <td valign="top" className="pe-2">
                    4.{" "}
                  </td>
                  <td>
                    {intl.formatMessage({ id: "reinstatementJobScope4" })}
                  </td>
                </tr>
                <tr>
                  <td valign="top" className="pe-2">
                    5.{" "}
                  </td>
                  <td>
                    {intl.formatMessage({ id: "reinstatementJobScope5" })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="my-4 text-sm">
            <div className="flex items-center justify-between border-t border-b border-black">
              <div className="w-1/2 py-4 font-bold tracking-wide uppercase border-r border-black">
                {intl.formatMessage({ id: "subTotal" })}
              </div>
              <div>RM{formatPayment(totalRef.current)}</div>
            </div>
            {/* <div className="flex items-center justify-between">
              <div className="w-1/2 py-4 font-bold border-r border-black">
                SST (6%)
              </div>
              <div>RM{formatPayment(totalRef.current * 0.06)}</div>
            </div> */}
            <div className="flex items-center justify-between font-bold tracking-wide uppercase border-b border-black">
              <div className="w-1/2 py-4 border-r border-black">
                {intl.formatMessage({ id: "total" })}
              </div>
              <div>RM{formatPayment(totalRef.current)}</div>
            </div>
          </div>
          <i className="block mb-10">
            <div className="flex text-sm">
              <div className="pe-5">
                {intl.formatMessage({ id: "remarks" })}:
              </div>
              <table>
                <tbody>
                  <tr>
                    <td valign="top" className="pe-2">
                      -{" "}
                    </td>
                    <td>
                      {intl.formatMessage(
                        { id: "remarks1" },
                        {
                          X: intl
                            .formatMessage({
                              id: isInvoice ? "proformaInvoice" : "quotation",
                            })
                            .toLowerCase(),
                        }
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      -{" "}
                    </td>
                    <td>
                      {intl.formatMessage(
                        { id: "remarks2" },
                        {
                          X: intl
                            .formatMessage({
                              id: isInvoice ? "proformaInvoice" : "quotation",
                            })
                            .toLowerCase(),
                        }
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      -{" "}
                    </td>
                    <td>{intl.formatMessage({ id: "remarks3" })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </i>
        </div>
        <div ref={section2Ref}>
          {isInvoice && (
            <div className="p-2 mb-10 border border-black">
              <div className="mb-4 font-bold tracking-wide uppercase">
                <span className="underline">
                  {intl.formatMessage({ id: "paymentDetails" })}
                </span>
              </div>
              <table>
                <tbody>
                  <tr>
                    <td valign="top" className="pe-2">
                      1.{" "}
                    </td>
                    <td valign="top" className="w-1 text-nowrap">
                      {intl.formatMessage({ id: "bankAccountName" })}
                    </td>
                    <td className="px-1">:</td>
                    <td>
                      <b>AD CONCEPT & PRODUCTION SDN BHD</b>
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      2.{" "}
                    </td>
                    <td className="w-1 text-nowrap">
                      {intl.formatMessage({ id: "bankName" })}
                    </td>
                    <td className="px-1">:</td>
                    <td>
                      <b>Maybank</b>
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      3.{" "}
                    </td>
                    <td className="w-1 text-nowrap">
                      {intl.formatMessage({ id: "bankAccountNumber" })}
                    </td>
                    <td className="px-1">:</td>
                    <td>
                      <b>562666058400</b>
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      4.{" "}
                    </td>
                    <td colSpan={3}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: intl.formatMessage({ id: "paymentInfo" }),
                        }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div className="text-sm font-bold tracking-wide uppercase">
            {intl.formatMessage({ id: "importantNotes" })}
          </div>
          <div className="md:flex md:justify-between">
            <div
              className={`pb-4 ${!isInvoice ? "md:w-2/3" : ""} md:pb-0 text-xs`}
            >
              <table>
                <tbody>
                  <tr>
                    <td valign="top" className="pe-2">
                      1.{" "}
                    </td>
                    <td>
                      {intl.formatMessage(
                        { id: "notes1" },
                        {
                          X: intl
                            .formatMessage({
                              id: isInvoice ? "order" : "quotation",
                            })
                            .toLowerCase(),
                        }
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      2.{" "}
                    </td>
                    <td>{intl.formatMessage({ id: "notes2" })}</td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      3.{" "}
                    </td>
                    <td>
                      {intl.formatMessage(
                        { id: "notes3" },
                        {
                          X: intl
                            .formatMessage({
                              id: isInvoice ? "order" : "quotation",
                            })
                            .toLowerCase(),
                        }
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      4.{" "}
                    </td>
                    <td>{intl.formatMessage({ id: "notes4" })}</td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      5.{" "}
                    </td>
                    <td>{intl.formatMessage({ id: "notes5" })}</td>
                  </tr>
                  <tr>
                    <td valign="top" className="pe-2">
                      6.{" "}
                    </td>
                    <td>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: intl.formatMessage({ id: "notes6" }),
                        }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {!isInvoice && (
              <div className="flex flex-col items-stretch h-full">
                <div className="flex flex-col items-stretch justify-between w-64 p-2 border-2 border-black h-2/3">
                  <div className="text-xs">
                    {intl.formatMessage({ id: "confirmThisQuotation" })}
                  </div>
                  <div>
                    <div className="h-12 mx-2 border-b border-black" />
                    <div className="text-xs text-center">
                      <i>({intl.formatMessage({ id: "companySign" })})</i>
                    </div>
                    <div className="mt-2 text-sm tracking-wide uppercase col-3">
                      {intl.formatMessage({ id: "name" })}:
                    </div>
                    <div className="text-sm tracking-wide uppercase col-3">
                      {intl.formatMessage({ id: "date" })}:
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  {intl.formatMessage({ id: "disignation" })}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="py-14" ref={additionalPaddingRef} />
      </div>
      <div className="fixed bottom-0 left-0 right-0 flex justify-between p-4 bg-white border-t shadow-lg">
        <button
          className="bg-[#6b5c45] border-2 border-[#3f3525] text-white px-8 py-2 rounded hover:bg-[#3f3525] transition font-semibold"
          onClick={() => setIsInvoice((prev) => !prev)}
        >
          {intl.formatMessage({
            id: isInvoice ? "backToQuotation" : "generateProformaInvoice",
          })}
        </button>
        <button
          className="bg-[#6b5c45] border-2 border-[#3f3525] text-white px-8 py-2 rounded hover:bg-[#3f3525] transition font-semibold"
          onClick={handlePrint}
        >
          {intl.formatMessage({ id: "download" })}
        </button>
      </div>
    </div>
  );
};
